import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Pencil } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PLATFORM_TAGS, PLATFORM_TAG_COPY, type FeatureFlagDefinition } from '@/types/feature-flag';
import { FlagWriteConfirmDialog } from './flag-write-confirm-dialog';

const formSchema = z.object({
  label: z
    .string()
    .trim()
    .min(1, 'Label is required')
    .max(128, 'Label must be 128 characters or fewer'),
  description: z.string().max(2000, 'Description must be 2000 characters or fewer'),
  platformTag: z.enum(PLATFORM_TAGS),
});

type FormValues = z.infer<typeof formSchema>;

export interface FlagMetadataBody {
  label: string;
  description: string | null;
  platformTag: FormValues['platformTag'];
}

interface FlagMetadataDialogProps {
  /** The flag being edited, or null when the dialog is closed. */
  definition: FeatureFlagDefinition | null;
  prodGuard: boolean;
  busy: boolean;
  error: Error | null;
  onSubmit: (body: FlagMetadataBody) => void;
  onClose: () => void;
}

/**
 * Edits a flag's description, label and platform tag.
 *
 * None of these change what the backend does with the flag — they are what an
 * operator reads before deciding to touch it. Enforced and built-in flags are
 * editable here for that reason, unlike the overrides and rules panels, which
 * those flags reject.
 */
export function FlagMetadataDialog({
  definition,
  prodGuard,
  busy,
  error,
  onSubmit,
  onClose,
}: FlagMetadataDialogProps) {
  const [pending, setPending] = useState<FlagMetadataBody | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { label: '', description: '', platformTag: 'both' },
  });

  // The dialog outlives any one flag, so re-seed the form each time it opens.
  useEffect(() => {
    if (definition) {
      reset({
        label: definition.label,
        description: definition.description ?? '',
        platformTag: definition.platformTag,
      });
    }
  }, [definition, reset]);

  const closeDialog = () => {
    setPending(null);
    onClose();
  };

  const submit = (values: FormValues) => {
    const body: FlagMetadataBody = {
      label: values.label,
      description: values.description.trim() || null,
      platformTag: values.platformTag,
    };
    if (prodGuard) {
      setPending(body);
      return;
    }
    onSubmit(body);
  };

  return (
    <>
      <Dialog
        open={definition !== null}
        onOpenChange={next => {
          // Cancel is disabled while saving; Escape and the overlay must not
          // be a way around that, or a failed save loses everything typed.
          if (!next && !busy) closeDialog();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit {definition?.label ?? 'flag'}
            </DialogTitle>
            <DialogDescription>
              How this flag is described to operators. None of it changes who receives the flag or
              what the backend does with it.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(submit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="flag-meta-label">Label *</Label>
              <Input
                id="flag-meta-label"
                aria-invalid={errors.label ? true : undefined}
                aria-describedby={errors.label ? 'flag-meta-label-error' : undefined}
                {...register('label')}
              />
              {errors.label && (
                <p id="flag-meta-label-error" className="text-xs text-destructive">
                  {errors.label.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="flag-meta-description">Description</Label>
              <Textarea
                id="flag-meta-description"
                rows={4}
                placeholder="What this flag controls, and what happens when it is off."
                aria-invalid={errors.description ? true : undefined}
                aria-describedby={errors.description ? 'flag-meta-description-error' : undefined}
                {...register('description')}
              />
              {errors.description && (
                <p id="flag-meta-description-error" className="text-xs text-destructive">
                  {errors.description.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="flag-meta-platform">Affects</Label>
              <Controller
                control={control}
                name="platformTag"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="flag-meta-platform">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORM_TAGS.map(tag => (
                        <SelectItem key={tag} value={tag}>
                          {PLATFORM_TAG_COPY[tag].label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error.message}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <FlagWriteConfirmDialog
        open={pending !== null}
        title="Save flag details?"
        description={`This updates how ${definition?.key ?? 'this flag'} is described in the dashboard. It does not change the flag's value or who receives it.`}
        prodGuard={prodGuard}
        busy={busy}
        onConfirm={() => {
          if (pending) onSubmit(pending);
          setPending(null);
        }}
        onCancel={() => setPending(null)}
      />
    </>
  );
}
